import { useState, useRef, useCallback } from 'react';

export type RecorderState = 'idle' | 'recording' | 'processing';

export interface UseAudioRecorderOptions {
  /** Silence threshold in dB (default: -50). Below this = silence. */
  silenceThreshold?: number;
  /** Seconds of silence before auto-stop (default: 2) */
  silenceDuration?: number;
  /** Max recording duration in seconds (default: 60) */
  maxDuration?: number;
}

export interface UseAudioRecorderReturn {
  state: RecorderState;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  audioBlob: Blob | null;
  error: string | null;
  /** dB level 0-100 (for waveform animation) */
  level: number;
}

export function useAudioRecorder(options: UseAudioRecorderOptions = {}): UseAudioRecorderReturn {
  const {
    silenceThreshold = -50,
    silenceDuration = 2,
    maxDuration = 60,
  } = options;

  const [state, setState] = useState<RecorderState>('idle');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [level, setLevel] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const cleanup = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    streamRef.current = null;
    setLevel(0);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setAudioBlob(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;

      // ─── Web Audio API for silence detection ────────────────────────────
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Float32Array(analyser.fftSize);

      // ─── MediaRecorder ────────────────────────────────────────────────────
      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        setAudioBlob(blob);
        setState('idle');
      };

      recorder.start(100); // collect chunks every 100ms
      setState('recording');

      // ─── Silence detection loop ──────────────────────────────────────────
      let isSilent = false;

      const detectSilence = () => {
        if (!analyserRef.current) return;
        analyser.getFloatTimeDomainData(dataArray);

        // RMS → dB
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
        const rms = Math.sqrt(sum / dataArray.length);
        const db = rms > 0 ? 20 * Math.log10(rms) : -Infinity;

        // Level for UI (0-100)
        const normalizedLevel = Math.max(0, Math.min(100, ((db + 80) / 80) * 100));
        setLevel(normalizedLevel);

        const silent = db < silenceThreshold;

        if (silent && !isSilent) {
          isSilent = true;
          silenceTimerRef.current = setTimeout(() => {
            stopRecording();
          }, silenceDuration * 1000);
        } else if (!silent && isSilent) {
          isSilent = false;
          if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        }

        animFrameRef.current = requestAnimationFrame(detectSilence);
      };

      detectSilence();

      // ─── Max duration safeguard ──────────────────────────────────────────
      maxTimerRef.current = setTimeout(() => {
        stopRecording();
      }, maxDuration * 1000);

    } catch (err: any) {
      cleanup();
      setState('idle');
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Accès au microphone refusé. Vérifiez les permissions du navigateur.');
      } else {
        setError('Impossible d\'accéder au microphone.');
      }
    }
  }, [cleanup, stopRecording, silenceThreshold, silenceDuration, maxDuration]);

  return { state, startRecording, stopRecording, audioBlob, error, level };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSupportedMimeType(): string {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}
